import type { IconName } from './Icon'

/** A button in a toast (A09: "Show again" after a dismissal): it closes the toast and runs once. */
export interface ToastAction {
  label: string
  run: () => void
}

export interface ToastItem {
  id: number
  message: string
  icon: IconName
  action?: ToastAction
}

const DISPLAY_MS = 3200
/** A toast with an action stays longer, so it can be reached and used. */
const ACTION_MS = 8000

/*
 * A tiny module-level store rather than a context: toasts are raised from
 * event handlers anywhere in the tree, and a plain function call is the
 * simplest API for that. ToastHost reads it through useSyncExternalStore.
 */
let items: ToastItem[] = []
let nextId = 1
const listeners = new Set<() => void>()
const emit = () => listeners.forEach((listener) => listener())

export function toast(message: string, icon: IconName = 'checkCircle', action?: ToastAction): void {
  const id = nextId++
  items = [...items, action === undefined ? { id, message, icon } : { id, message, icon, action }]
  emit()

  window.setTimeout(() => dismissToast(id), action === undefined ? DISPLAY_MS : ACTION_MS)
}

/** Closes a toast before its time (its action was used). */
export function dismissToast(id: number): void {
  if (!items.some((item) => item.id === id)) return
  items = items.filter((item) => item.id !== id)
  emit()
}

export function subscribeToasts(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export const currentToasts = (): ToastItem[] => items
